import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc, FileMeta, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateBoolean, validateNumber, validateString } from '/imports/validators';
import { strings } from '/imports/strings';
import { optionsToDict } from '/imports/utils';
import { Project } from '/imports/api/projects';
import { EnvelopeResponse } from '/server/integrations/docusign';

export interface ProjectAgreement extends Doc {
  projectId: string;
  name: string;
  docx: FileMeta;
  file: FileMeta;
  fileHistory?: string[];
  type: 'siteLease' | 'solarPlusBusinessRateCommitment';
  status: 'notSent' | 'pendingLessor' | 'lessorSigned' | 'pendingLessee' | 'lesseeSigned' | 'signedAndSealed' | 'expired';
  docusign?: EnvelopeResponse;
}
export type Model = ProjectAgreement;

export const ProjectAgreements = new Mongo.Collection<ProjectAgreement>('projectAgreements');

type Refs = References<Model>

export const modelClass: ModelClass<Model, Refs> = {
  collection: ProjectAgreements,
  editableFields: ['active', 'projectId', 'name', 'file', 'type',
  ],
  fieldLimits: {
    'name': 256, 'projectId': 64, 'type': 128,
  },
  listMethod: DeclareMethod<ListMethodArg<ProjectAgreement>, ListMethodResponse<ProjectAgreement, Refs>>('listProjectAgreements'),
  getMethod: DeclareMethod<string, GetMethodResponse<ProjectAgreement, Refs>>('getProjectAgreement'),
  createMethod: DeclareMethod<Partial<ProjectAgreement>, string>('createProjectAgreement'),
  updateMethod: DeclareMethod<Partial<ProjectAgreement>, string>('updateProjectAgreement'),
  deleteMethod: DeclareMethod<string>('deleteProjectAgreement'),
  pathPrefix: '/project-agreements',
  asString: (document) => document.name || document._id,
  modelName: strings.Agreements(),
  pluralName: strings.Agreements(),
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { name: new RegExp(search, 'gi') },
      ]
    };
  },
  validate: async (project: Partial<ProjectAgreement>) => {
    const errors: { [key: string]: string[] } = {};
    validateBoolean(errors, project, 'active');
    validateString(errors, project, 'projectId', { required: true });
    validateString(errors, project, 'name', { required: true });
    validateString(errors, project, 'type', { required: true });
    validateString(errors, project, 'status', { required: true });
    return errors;
  },
};

export const generateAgreement = DeclareMethod<{ projectId: string, type: ProjectAgreement['type'] }, string>('generateAgreement');
export const sendAgreement = DeclareMethod<string, void>('sendAgreement');

export const AgreementTypeOptions = [
  { value: 'ceaSolarPlusRateForms', label: 'Solar Plus Rate Form' },
  { value: 'ceaCsspFormSiteLease', label: 'CEA Form Site Lease' },
];

const AgreementTypeDict = optionsToDict(AgreementTypeOptions);

/**
 * Resolves an agreement's type to its human-readable label.
 * @param projectAgreement - the agreement
 * @returns the label, falling back to the raw type value
 */
export const projectAgreementType = (projectAgreement: ProjectAgreement) => AgreementTypeDict[projectAgreement.type] || projectAgreement.type;

/**
 * Validates that a project has all lessor, lessee, lease, and property fields required to generate an agreement.
 * @param project - the project to validate
 * @returns map of field name to validation error messages
 */
export const validateProjectForAgreement = (project: Project) => {
  const errors: { [key: string]: string[] } = {};
  validateString(errors, project, 'lessorName', { required: true });
  validateString(errors, project, 'lessorEmail', { required: true });
  validateString(errors, project, 'lessorCompany', { required: true });
  validateString(errors, project, 'lessorCompanyParent', { required: true });
  validateString(errors, project, 'lessorAddress1', { required: true });
  validateString(errors, project, 'lessorAddress2', { required: true });
  validateString(errors, project, 'lessorAttentionName', { required: true });
  validateString(errors, project, 'lessorAttentionEmail', { required: true });
  validateString(errors, project, 'lesseeName', { required: true });
  validateString(errors, project, 'lesseeEmail', { required: true });
  validateString(errors, project, 'lesseeCompany', { required: true });
  validateString(errors, project, 'lesseeCompanyParent', { required: true });
  validateString(errors, project, 'leaseEffectiveDate', { required: true });
  validateString(errors, project, 'meterNumbers', { required: true });
  validateString(errors, project, 'ptoDate', { required: true });
  validateNumber(errors, project, 'initialRate', { required: true });
  validateNumber(errors, project, 'initialRateUpdated', { required: true });
  validateString(errors, project, 'termEndDate', { required: true });
  validateNumber(errors, project, 'renewalTermYears', { required: true });
  validateString(errors, project, 'maxTermEndDate', { required: true });
  validateNumber(errors, project, 'firstRentPayment', { required: true });
  validateString(errors, project, 'proCo', { required: true });
  validateString(errors, project, 'propertyLegalDescription', { required: true });
  validateString(errors, project, 'easementsMetesAndBounds', { required: true });
  validateString(errors, project, 'customerUtilityMeterNumbers', { required: true });
  validateString(errors, project, 'propertyApn', { required: true });
  validateString(errors, project, 'agreementExpirationDate', { required: true });
  return errors;
};

Meteor.isServer && Meteor.startup(async () => {
  await ProjectAgreements.createIndexAsync({ projectId: 1 });
  await ProjectAgreements.createIndexAsync({ name: 1 });
  await ProjectAgreements.createIndexAsync({ type: 1 });
});
